import * as React from 'react'

import { Button, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import type { Option } from 'modules/core/components/Popover/Content'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import users, { employeeUser } from 'modules/core/store/collections/users'

import { BecomeModal } from 'modules/authentication/public'
import { employeeReferenceContract } from 'modules/contracts/lib/utils'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import EditUserModal from 'modules/permissions/components/EditUserModal'
import RemoveAccessModal from 'modules/permissions/pages/settings/PermissionGroupPage/RemoveAccessModal'
import permissionGroups from 'modules/permissions/store/collections/permissionGroups'
import type PermissionGroup from 'modules/permissions/store/models/PermissionGroup'

import ForwardButton from './ForwardButton'
import styles from './index.module.scss'

interface Props {
  permissionGroup: PermissionGroup
  employee: Employee
  contractVersions: ContractVersions
}

const Item = ({ permissionGroup, employee, contractVersions }: Props) => {
  const { navigation } = useNavigation()

  const [editModalOpen, setEditModalOpen] = React.useState(false)
  const [removeModalOpen, setRemoveModalOpen] = React.useState(false)
  const [openBecome, toggleBecome] = React.useState(false)
  const [open, toggle] = useToggle(false)
  const user = employeeUser(employee)
  const redirect = navigation.employees.id.fullPath({ id: employee.id })

  const options = (): Option[] => {
    const allowedOptions = []

    if (users.current.policy.can('permissions.groupAssignEmployees')) {
      allowedOptions.push({
        to: navigation.settings.permissions.id.employees.userIdAssign.fullPath({
          id: permissionGroup.id,
          userId: user.id,
        }),
        title: i18n.t('settings.permissions.group.change_group'),
      })
    }

    if (employee.policy.can('editPersonal')) {
      allowedOptions.push({
        title: i18n.t('settings.permissions.users.edit'),
        onClick: () => {
          setEditModalOpen(true)
        },
      })
    }

    const employeeIsInAdminGroup = !!permissionGroups.adminGroup?.users.includes(
      employeeUser(employee)
    )
    const canBecome = hasFeature('become_enabled') && !employeeIsInAdminGroup

    // TODO: check a can become verb
    if (employee.policy.can('editPersonal') && canBecome) {
      allowedOptions.push({
        title: i18n.t('employees.become.action', { name: user.firstName }),
        onClick: () => {
          toggleBecome(true)
        },
      })
    }

    // Disabling this option for now till we decide on a convenient delete flow
    // if (can('terminateEmployee', employee) && permissionGroup.employees.length > 1) {
    //   allowedOptions.push({
    //     title: i18n.t('settings.permissions.users.delete'),
    //     onClick: () => {
    //       setRemoveModalOpen(true)
    //     },
    //     type: 'wrong'
    //   })
    // }

    return allowedOptions
  }

  return (
    <div className={styles.root}>
      <div className={styles.profile}>
        <div className={styles.avatar}>
          <UserAvatar user={user} />
        </div>
        <div>
          <div className={styles.name}>
            <div className={styles.title}>{user.fullName()}</div>
          </div>
          <div className={styles.jobTitle}>
            <div className={styles.title}>
              {employeeReferenceContract(contractVersions, employee)?.attributes.job_title}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.options}>
        {options().length > 0 && (
          <Popover
            attachment="top right"
            targetAttachment="bottom center"
            toggle={toggle}
            open={open}
          >
            <Button type="secondary" onClick={toggle(true)} iconName="ellipsis" />
            <PopoverContent onClose={toggle(false)} options={options()} />
          </Popover>
        )}
        <ForwardButton employee={employee} redirect={redirect} />
      </div>
      {editModalOpen && (
        <EditUserModal
          onClose={() => setEditModalOpen(false)}
          permissionGroup={permissionGroup}
          user={user}
        />
      )}
      {removeModalOpen && (
        <RemoveAccessModal user={user} onClose={() => setRemoveModalOpen(false)} />
      )}
      {openBecome && (
        <BecomeModal
          accessId={user.id}
          firstName={user.firstName}
          onClose={() => toggleBecome(false)}
        />
      )}
    </div>
  )
}

export default observer(Item)
